import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { getContradictionEscalation, getEscalationUI } from '../../../utils/deadlineRules';

const { FiCheckSquare, FiSquare, FiShield, FiInfo, FiCalendar, FiAlertCircle, FiClock, FiCheckCircle } = FiIcons;

const DEFAULT_ITEMS = [
  { id: "id_parties", label: "Identifisere omvarslede parter", completed: false, date: null },
  { id: "timing", label: "Vurdere tidspunkt for kontradiksjon", completed: false, date: null },
  { id: "notify", label: "Varsle omvarslede om påstandene", completed: false, date: null },
  { id: "opportunity", label: "Gi anledning til å uttale seg", completed: false, date: null },
  { id: "document", label: "Dokumentere tilsvaret", completed: false, date: null },
  { id: "evaluate", label: "Vurdere behov for ytterligere undersøkelser", completed: false, date: null }
];

const ContradictionChecklist = ({ report }) => {
  const { updateReport } = useData();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);
  const [tempDeadline, setTempDeadline] = useState(report.deadline_reported_reply ? report.deadline_reported_reply.split('T')[0] : '');

  useEffect(() => {
    if (report.contradiction_checklist && Array.isArray(report.contradiction_checklist) && report.contradiction_checklist.length > 0) {
      setItems(report.contradiction_checklist);
    } else {
      setItems(DEFAULT_ITEMS);
    }
  }, [report.contradiction_checklist]);

  const handleToggle = async (itemId) => {
    if (saving) return;
    
    const newItems = items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          completed: !item.completed,
          date: !item.completed ? new Date().toISOString() : null,
          user: !item.completed ? user.name : null
        };
      }
      return item;
    });

    setItems(newItems);
    setSaving(true);

    const completedCount = newItems.filter(i => i.completed).length;
    let status = 'ikke_påbegynt';
    if (completedCount === newItems.length) status = 'fullført';
    else if (completedCount > 0) status = 'pågår';

    try {
      const toggledItem = newItems.find(i => i.id === itemId);
      const actionText = toggledItem.completed 
        ? `Fullførte punkt i kontradiksjon: ${toggledItem.label}`
        : `Angret punkt i kontradiksjon: ${toggledItem.label}`;

      const updateData = {
        contradiction_checklist: newItems,
        contradiction_status: status,
        history: [
          ...(report.history || []),
          {
            date: new Date().toISOString(),
            action: actionText,
            user: user.name
          }
        ]
      };

      if (itemId === 'notify' && toggledItem.completed && !report.reported_notified_at) {
        updateData.reported_notified_at = new Date().toISOString();
        if (!report.deadline_reported_reply) {
          const defaultDeadline = new Date();
          defaultDeadline.setDate(defaultDeadline.getDate() + 14);
          updateData.deadline_reported_reply = defaultDeadline.toISOString();
        }
      }

      await updateReport(report.id, updateData);
    } catch (err) {
      console.error("Error updating checklist:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDeadline = async () => {
    if (!tempDeadline) return;
    setSaving(true);
    try {
      await updateReport(report.id, {
        deadline_reported_reply: new Date(tempDeadline).toISOString(),
        history: [
          ...(report.history || []),
          {
            date: new Date().toISOString(),
            action: `Satte svarfrist for omvarslet person til ${format(new Date(tempDeadline), 'dd.MM.yyyy')}`,
            user: user.name
          }
        ]
      });
      setShowDeadlinePicker(false);
    } catch (err) {
      console.error("Error updating deadline:", err);
    } finally {
      setSaving(false);
    }
  };

  const completedCount = items.filter(i => i.completed).length;
  const progress = (completedCount / items.length) * 100;
  
  // Eskaleringslogikk
  const escalationStatus = getContradictionEscalation(report.deadline_reported_reply);
  const escalationUI = getEscalationUI(escalationStatus);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[24px] border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-50 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-black text-gray-900 dark:text-white flex items-center gap-2 uppercase text-xs tracking-widest">
            <SafeIcon icon={FiShield} className="text-indigo-600" />
            Kontradiksjonssjekkliste
          </h3>
          <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
            progress === 100 ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'
          }`}>
            {Math.round(progress)}% Fullført
          </span>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-indigo-600"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Eskaleringsvarsel */}
      {report.deadline_reported_reply && (
        <div className={`px-6 py-4 border-b ${escalationUI.color} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiIcons[escalationUI.icon]} size={18} />
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest">{escalationUI.label}</p>
              <p className="text-xs font-bold">
                Svarfrist: {format(new Date(report.deadline_reported_reply), 'dd. MMMM yyyy', { locale: nb })}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setShowDeadlinePicker(!showDeadlinePicker)}
            className="text-[10px] font-black uppercase tracking-widest underline"
          >
            Endre frist
          </button>
        </div>
      )}

      {!report.deadline_reported_reply && (
        <div className="px-6 py-4 bg-amber-50/50 dark:bg-amber-900/10 border-b border-amber-100/50 dark:border-amber-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-800/50 text-amber-600 rounded-lg">
                <SafeIcon icon={FiCalendar} size={16} />
              </div>
              <div>
                <p className="text-[10px] font-black text-amber-800 dark:text-amber-400 uppercase tracking-widest">Svarfrist: Varslet Person</p>
                <p className="text-sm font-bold text-amber-900 dark:text-amber-100">Ikke satt</p>
              </div>
            </div>
            <button 
              onClick={() => setShowDeadlinePicker(!showDeadlinePicker)}
              className="text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700 underline"
            >
              Sett frist
            </button>
          </div>
        </div>
      )}

      {showDeadlinePicker && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex gap-2">
          <input 
            type="date" 
            value={tempDeadline}
            onChange={(e) => setTempDeadline(e.target.value)}
            className="flex-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-xs font-bold"
          />
          <button 
            onClick={handleUpdateDeadline}
            disabled={saving || !tempDeadline}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest"
          >
            Lagre
          </button>
        </div>
      )}

      <div className="p-4 space-y-1">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => handleToggle(item.id)}
            disabled={saving}
            className={`w-full flex items-start gap-4 p-4 rounded-2xl transition-all text-left group ${
              item.completed 
                ? 'bg-green-50/30 dark:bg-green-900/10' 
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            <div className={`mt-0.5 transition-colors ${item.completed ? 'text-green-600' : 'text-gray-300 group-hover:text-indigo-400'}`}>
              <SafeIcon icon={item.completed ? FiCheckSquare : FiSquare} size={20} />
            </div>
            <div>
              <p className={`text-sm font-bold transition-colors ${
                item.completed ? 'text-green-800 dark:text-green-200 line-through opacity-70' : 'text-gray-700 dark:text-gray-300'
              }`}>
                {item.label}
              </p>
              {item.completed && item.date && (
                <p className="text-[10px] text-green-600 dark:text-green-400 font-medium mt-1">
                  Fullført {new Date(item.date).toLocaleDateString('nb-NO')} av {item.user}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="p-6 bg-indigo-50/50 dark:bg-indigo-900/10 border-t border-indigo-100/50 dark:border-indigo-800/50 flex gap-3">
        <SafeIcon icon={FiInfo} className="text-indigo-600 shrink-0" size={18} />
        <p className="text-[11px] text-indigo-800 dark:text-indigo-300 leading-relaxed font-medium">
          Kontradiksjon er retten til å bli gjort kjent med anklager og få uttale seg. 
          Dette er et grunnleggende rettssikkerhetsprinsipp i alle undersøkelser.
        </p>
      </div>
    </div>
  );
};

export default ContradictionChecklist;